import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send, Navigation, Star } from "lucide-react";
import { Reveal, PageHeader, SectionLabel } from "@/components/Reveal";
import { CONTACT, SERVICES } from "@/data/content";

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_API_URL || "/api/contact.php";

const inputCls =
  "w-full bg-white border border-border px-4 py-3 text-sm text-ink placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition-colors duration-300";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(CONTACT_ENDPOINT, form);
      toast.success("Thank you — your enquiry has been received. We will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "", website: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-border p-8 sm:p-10" data-testid="contact-form">
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={form.website}
        onChange={set("website")}
        data-testid="contact-honeypot-input"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", top: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="contact-name" className="block text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">Name *</label>
          <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">Email *</label>
          <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
        </div>
        <div>
          <label htmlFor="contact-phone" className="block text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">Phone</label>
          <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 …" />
        </div>
        <div>
          <label htmlFor="contact-service" className="block text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">Service Required</label>
          <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
            <option value="">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s.title} value={s.title}>{s.title}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-6">
        <label htmlFor="contact-message" className="block text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">Message *</label>
        <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your valuation requirement…" />
      </div>
      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={sending}
        className="mt-8 bg-ink text-white text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 inline-flex items-center gap-2 hover:bg-gold disabled:opacity-60 transition-colors duration-300"
      >
        {sending ? "Sending…" : "Send Enquiry"} <Send size={14} />
      </button>
    </form>
  );
};

const ContactDetails = () => (
  <div className="space-y-px bg-border border border-border" data-testid="contact-details">
    <div className="bg-white p-8">
      <MapPin size={18} className="text-gold" />
      <p className="mt-4 text-xs tracking-[0.25em] uppercase font-semibold text-slate-500">Registered Office</p>
      <p className="mt-2 text-sm leading-relaxed text-ink" data-testid="contact-address">{CONTACT.address}</p>
    </div>
    <div className="bg-white p-8">
      <Mail size={18} className="text-gold" />
      <p className="mt-4 text-xs tracking-[0.25em] uppercase font-semibold text-slate-500">Email</p>
      <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="mt-2 block text-sm text-ink hover:text-gold transition-colors duration-300">{CONTACT.email}</a>
      {CONTACT.altEmails.map((e) => (
        <a key={e} href={`mailto:${e}`} data-testid={`contact-alt-email-${e.split("@")[0]}`} className="mt-1 block text-sm text-slate-500 hover:text-gold transition-colors duration-300">{e}</a>
      ))}
    </div>
    <div className="bg-white p-8">
      <Phone size={18} className="text-gold" />
      <p className="mt-4 text-xs tracking-[0.25em] uppercase font-semibold text-slate-500">Call</p>
      <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="mt-2 block text-sm text-ink hover:text-gold transition-colors duration-300">{CONTACT.phone}</a>
      <div className="mt-1 flex gap-4">
        {CONTACT.altPhones.map((p) => (
          <a key={p} href={`tel:${p.replace(/\s/g, "")}`} data-testid={`contact-alt-phone-${p.slice(-4)}`} className="text-sm text-slate-500 hover:text-gold transition-colors duration-300">{p}</a>
        ))}
      </div>
    </div>
  </div>
);

export default function Contact() {
  return (
    <>
      <PageHeader
        label="Contact Us"
        lines={["Let's talk about", "your valuation needs."]}
        sub="Reach out for property, plant & machinery, business or statutory valuation assignments across Rajasthan and beyond."
      />
      <section className="py-24 px-6 sm:px-12" data-testid="contact-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>Send an Enquiry</SectionLabel>
              <h2 className="mt-4 mb-10 font-serif text-2xl sm:text-3xl tracking-tight text-ink">
                We respond within one business day.
              </h2>
              <ContactForm />
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <SectionLabel>Reach Us Directly</SectionLabel>
              <div className="mt-4 lg:mt-[52px]">
                <ContactDetails />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="pb-24 px-6 sm:px-12" data-testid="map-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <SectionLabel>Find Us</SectionLabel>
            <h2 className="mt-4 mb-8 font-serif text-2xl sm:text-3xl tracking-tight text-ink">
              Visit our Jaipur office.
            </h2>
            <div className="border border-border bg-white p-2">
              <iframe
                title="Auric Valuation office — Plot No. 9, Samta Nagar, Vaishali Marg West, Jaipur"
                data-testid="office-map"
                src="https://maps.google.com/maps?q=26.9141528,75.7136144&t=&z=17&ie=UTF8&iwloc=&output=embed"
                className="w-full h-[420px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <p className="mt-4 text-sm text-slate-500" data-testid="map-address">
              Auric Valuation Pvt. Ltd. — Ground Floor, Plot No. 9, Samta Nagar, Vaishali Marg West, Jaipur, Rajasthan 302034
            </p>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=26.9141528,75.7136144"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="get-directions-button"
              className="mt-6 inline-flex items-center gap-2 bg-ink text-white text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 hover:bg-gold transition-colors duration-300"
            >
              <Navigation size={14} /> Get Directions
            </a>
            <div className="mt-10 bg-white border border-border p-8 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="google-review-card">
              <div className="flex gap-1 shrink-0" aria-label="5 star rating">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={22} className="fill-gold text-gold" />
                ))}
              </div>
              <div className="flex-1">
                <p className="font-serif text-lg font-bold text-ink">Happy with our service?</p>
                <p className="mt-1 text-sm text-slate-500">
                  Your feedback helps others find a valuation partner they can trust. It takes less than a minute.
                </p>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=26.9141528,75.7136144"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-button"
                className="inline-flex items-center gap-2 bg-gold text-white text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 hover:bg-ink transition-colors duration-300 shrink-0"
              >
                Leave a Google Review
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
